import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, Mail, AlertCircle, Loader2, Shield, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { apiService } from '../services/api';
import Swal from 'sweetalert2';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimeLeft, setBlockTimeLeft] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [systemLogo, setSystemLogo] = useState<string | null>(null);
  const [systemTitle, setSystemTitle] = useState('IT Toolbox');
  const [loginBackground, setLoginBackground] = useState<string | null>(null);
  const { login, isLoading } = useAuth();
  const { hasUpdate, currentVersion, latestVersion, handleUpdate, dismissUpdate } = useVersionCheck();
  const { language, setLanguage, t } = useLanguage();

  // System settings yükle
  useEffect(() => {
    loadSystemSettings();
    loadLoginBackground();

    // Logo güncelleme event listener'ı
    const handleLogoUpdate = (event: CustomEvent) => {
      console.log('LoginPage logo update event received:', event.detail);
      if (event.detail?.logoPath) {
        setSystemLogo(event.detail.logoPath);
      } else {
        // Eğer detail yoksa ayarları yeniden yükle
        loadSystemSettings();
      }
    };

    window.addEventListener('logoUpdated', handleLogoUpdate as EventListener);

    return () => {
      window.removeEventListener('logoUpdated', handleLogoUpdate as EventListener);
    };
  }, []);

  const loadSystemSettings = async () => {
    try {
      // Önce dosyayı direkt kontrol et
      const timestamp = new Date().getTime();
      try {
        const logoResponse = await fetch(`/uploads/logo.png?t=${timestamp}`);
        if (logoResponse.ok) {
          setSystemLogo(`/uploads/logo.png?t=${timestamp}`);
        } else {
          setSystemLogo(null);
        }
      } catch (error) {
        setSystemLogo(null);
      }

      // System settings'i yükle (title için) - auth olmadan
      try {
        const response = await fetch('/api/system-settings/public');
        if (response.ok) {
          const settings = await response.json();
          if (Array.isArray(settings)) {
            // Title ayarını bul
            const titleSetting = settings.find((s: any) => s.setting_key === 'system_title');
            if (titleSetting && titleSetting.setting_value) {
              setSystemTitle(titleSetting.setting_value);
            }
          }
        }
      } catch (error) {
        console.error('Error loading system settings:', error);
      }
    } catch (error) {
      console.error('Error loading system settings:', error);
      // Hata durumunda varsayılan değerleri kullan
      setSystemTitle('IT Toolbox');
    }
  };

  const loadLoginBackground = async () => {
    try {
      const bgResponse = await fetch('/api/system-settings/public/login-background-exists');

      if (bgResponse.ok) {
        const bgData = await bgResponse.json();
        if (bgData.exists && bgData.path) {
          const timestamp = new Date().getTime();
          setLoginBackground(`${bgData.path}?t=${timestamp}`);
        }
      }
    } catch (error) {
      console.error('Login background yükleme hatası:', error);
    }
  };

  // Engelleme durumunu kontrol et
  React.useEffect(() => {
    if (!email) return;

    const blockData = localStorage.getItem(`login_block_${email}`);
    if (blockData) {
      const { blockedUntil, attempts } = JSON.parse(blockData);
      const now = Date.now();
      if (now < blockedUntil) {
        setIsBlocked(true);
        setFailedAttempts(attempts);
        setBlockTimeLeft(Math.ceil((blockedUntil - now) / 1000));
      } else {
        localStorage.removeItem(`login_block_${email}`);
        setFailedAttempts(0);
        setIsBlocked(false);
      }
    } else {
      setFailedAttempts(0);
      setIsBlocked(false);
    }
  }, [email]);

  // Geri sayım timer
  React.useEffect(() => {
    if (isBlocked && blockTimeLeft > 0) {
      const timer = setInterval(() => {
        setBlockTimeLeft(prev => {
          if (prev <= 1) {
            setIsBlocked(false);
            setFailedAttempts(0);
            localStorage.removeItem(`login_block_${email}`);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isBlocked, blockTimeLeft, email]);

  const formatBlockTime = (seconds: number) => {
    return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isBlocked) {
      setError(t('login.tooManyAttempts', { time: formatBlockTime(blockTimeLeft) }));
      return;
    }

    if (!email || !password) {
      setError(t('login.emailRequired'));
      return;
    }

    try {
      const result = await login(email, password, twoFactorCode);

      if (result === true) {
        localStorage.removeItem(`login_block_${email}`);
        setFailedAttempts(0);
        showGlobalNotification.success(t('login.success'), t('login.loggedIn'));
        // sessionStorage'da kayıtlı hedef URL varsa oraya yönlendir (paylaşılan linkler vb.)
        const redirectTo = sessionStorage.getItem('redirectAfterLogin') || '/';
        sessionStorage.removeItem('redirectAfterLogin');
        sessionStorage.setItem('justLoggedIn', '1');
        window.location.href = redirectTo;
      } else if (typeof result === 'object' && result.requiresTwoFactor) {
        setShowTwoFactor(true);
        setError('');
      }
    } catch (err: any) {
      const errorMessage = err.message || t('login.loginFailed');
      setError(errorMessage);

      Swal.fire({
        icon: 'error',
        title: t('login.loginError'),
        text: errorMessage,
        confirmButtonText: t('login.ok'),
        confirmButtonColor: '#3b82f6'
      });
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordMessage('');

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error || t('forgot.error');
        setForgotPasswordMessage(errorMessage);
        showGlobalNotification.error(t('common.error'), errorMessage);
        return;
      }

      if (data.success) {
        setForgotPasswordMessage(data.message);
        showGlobalNotification.success(t('common.success'), data.message);
      } else {
        const errorMessage = data.error || t('forgot.error');
        setForgotPasswordMessage(errorMessage);
        showGlobalNotification.error(t('common.error'), errorMessage);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMessage = t('forgot.sendError');
      setForgotPasswordMessage(errorMessage);
      showGlobalNotification.error(t('common.error'), errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex">
      {/* Sol taraf - Logo ve Login Form */}
      <div className="w-full lg:w-1/4 flex items-center justify-center px-4 py-6 sm:p-6 lg:p-4 bg-white">
        <div className="w-full max-w-xs lg:max-w-none lg:w-full space-y-6 lg:space-y-8">
          {/* Language Switcher */}
          <div className="flex justify-end">
            <div className="flex items-center space-x-1 bg-gray-100 rounded-full p-1">
              <Globe className="w-3.5 h-3.5 text-gray-500 ml-1.5" />
              <button
                type="button"
                onClick={() => setLanguage('tr')}
                className={`px-2.5 py-1 text-xs font-medium rounded-full transition-all ${
                  language === 'tr'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                TR
              </button>
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`px-2.5 py-1 text-xs font-medium rounded-full transition-all ${
                  language === 'en'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                EN
              </button>
            </div>
          </div>

          {/* Logo and Title */}
          <div className="text-center">
            <div className="mx-auto w-28 h-28 sm:w-32 sm:h-32 lg:w-40 lg:h-40 bg-white rounded-2xl flex items-center justify-center mb-6 lg:mb-8 overflow-hidden shadow-lg hover:scale-110 hover:shadow-2xl transition-all duration-300 cursor-pointer group">
              {systemLogo ? (
                <img
                  src={systemLogo}
                  alt="System Logo"
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  onError={() => setSystemLogo(null)}
                />
              ) : (
                <Shield className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 text-primary group-hover:text-primary-dark transition-colors duration-300" />
              )}
            </div>
            <h2 className="text-xl sm:text-2xl lg:text-2xl font-bold text-gray-900 mb-3 lg:mb-4">{systemTitle}</h2>
            <p className="text-sm sm:text-base lg:text-sm text-gray-600">{t('login.systemDescription')}</p>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-6 border border-gray-100">
          {showForgotPassword ? (
            /* Forgot Password Form */
            <form onSubmit={handleForgotPassword} className="space-y-5 lg:space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('forgot.title')}</h3>
                <p className="text-sm text-gray-600">{t('forgot.description')}</p>
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('forgot.emailLabel')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="forgot-email"
                    name="forgot-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder={t('login.emailPlaceholder')}
                  />
                </div>
              </div>

              {/* Message */}
              {forgotPasswordMessage && (
                <div className="flex items-center space-x-2 text-primary bg-primary/10 p-3 rounded-lg">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{forgotPasswordMessage}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={forgotPasswordLoading}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {forgotPasswordLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('forgot.sending')}
                  </>
                ) : (
                  t('forgot.submitButton')
                )}
              </button>

              {/* Back to Login */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                    setForgotPasswordMessage('');
                  }}
                  className="text-sm text-primary hover:text-primary transition-colors"
                >
                  {t('forgot.backToLogin')}
                </button>
              </div>
            </form>
          ) : (
            /* Login Form */
            <form onSubmit={handleSubmit} className="space-y-5 lg:space-y-4">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('login.emailLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder={t('login.emailPlaceholder')}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {t('login.passwordLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder={t('login.passwordPlaceholder')}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {/* 2FA Field */}
            {showTwoFactor && (
              <div>
                <label htmlFor="twoFactorCode" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('login.twoFactorLabel')}
                </label>
                <input
                  id="twoFactorCode"
                  name="twoFactorCode"
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-center text-lg tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  required
                />
                <p className="text-sm text-gray-600 mt-1">
                  {t('login.twoFactorHint')}
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Failed Attempts Warning */}
            {failedAttempts > 0 && !isBlocked && (
              <div className="flex items-center space-x-2 text-orange-600 bg-orange-50 p-3 rounded-lg">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">
                  {t('login.failedAttempts', { count: failedAttempts, remaining: 5 - failedAttempts })}
                </span>
              </div>
            )}

            {/* Block Warning */}
            {isBlocked && (
              <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">
                  {t('login.blockedMessage', { time: formatBlockTime(blockTimeLeft) })}
                </span>
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading || isBlocked}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('login.loggingIn')}
                </>
              ) : isBlocked ? (
                t('login.accountBlocked')
              ) : (
                t('login.loginButton')
              )}
            </button>

            {/* Forgot Password Link */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-primary hover:text-primary transition-colors"
              >
                {t('login.forgotPassword')}
              </button>
            </div>
          </form>
          )}
          </div>

          {/* Footer */}
          <div className="text-center text-gray-400 text-sm mt-6">
            <p>{t('login.copyright')}</p>
          </div>
        </div>
      </div>

      {/* Version Update Modal */}
      {hasUpdate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-primary" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">{t('update.title')}</h3>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                {t('update.description')}
              </p>
              <div className="text-xs text-gray-500 space-y-1">
                <div>{t('update.current')} {currentVersion}</div>
                <div>{t('update.new')} {latestVersion}</div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={dismissUpdate}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                {t('update.later')}
              </button>
              <button
                onClick={handleUpdate}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark transition-colors flex items-center justify-center"
              >
                <Loader2 className="w-4 h-4 mr-2" />
                {t('update.update')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sağ taraf - Background Görseli */}
      <div className="hidden lg:flex lg:w-3/4 relative min-h-screen">
        {loginBackground ? (
          <div
            className="w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${loginBackground})` }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 flex items-center justify-center">
            <div className="text-center text-white p-8">
              <Shield className="w-24 h-24 mx-auto mb-6 opacity-50" />
              <h3 className="text-2xl font-bold mb-4">IT Toolbox</h3>
              <p className="text-lg opacity-75">{t('login.systemDescription')}</p>
            </div>
          </div>
        )}
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-20" />
      </div>
    </div>
  );
};

export default LoginPage;
